import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { Cliente } from "../hooks/useClienteSearch";
import { useClienteSearch } from "../hooks/useClienteSearch";

interface ClienteAutocompleteProps {
  onSelect: (cliente: Cliente) => void;
  /** Limpia cliente seleccionado y datos del titular en el padre (hook). */
  onClear: () => void;
  /**
   * Si es false, al borrar solo el texto del buscador no se llama onClear
   * (evita borrar nombre/DNI escritos a mano sin haber elegido cliente).
   */
  hasSelectedCliente: boolean;
  /** Cliente fijado desde búsqueda: no permitir cambiar Booking / Airbnb aquí. */
  titularFieldsLocked?: boolean;
  esBooking: boolean;
  onEsBookingChange: (esBooking: boolean) => void;
  esAirbnb: boolean;
  onEsAirbnbChange: (esAirbnb: boolean) => void;
  errors?: {
    nombre?: string;
    apellido?: string;
  };
}

const ClienteAutocomplete = React.memo(
  ({
    onSelect,
    onClear,
    hasSelectedCliente,
    titularFieldsLocked = false,
    esBooking,
    onEsBookingChange,
    esAirbnb,
    onEsAirbnbChange,
    errors,
  }: ClienteAutocompleteProps) => {
    const { loading, sugerencias, buscarClientes } = useClienteSearch();
    const [clienteInput, setClienteInput] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        if (clienteInput.length >= 3) {
          buscarClientes(clienteInput);
        }
      }, 300);

      return () => clearTimeout(timer);
    }, [clienteInput, buscarClientes]);

    const handleClienteSelect = (cliente: Cliente) => {
      onSelect(cliente);
      setClienteInput(`${cliente.nombre} ${cliente.apellido}`);
    };

    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ mb: { xs: 0.75, sm: 1 } }}>
          <Autocomplete
            freeSolo
            disablePortal
            inputValue={clienteInput}
            onInputChange={(_, value, reason) => {
              setClienteInput(value);
              if (value !== "") return;
              if (!hasSelectedCliente) return;
              if (reason === "clear" || reason === "input") {
                onClear();
              }
            }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            loading={loading}
            noOptionsText={
              loading ? "Buscando..." : "No se encontraron clientes"
            }
            filterOptions={(x) => x}
            ListboxProps={{ style: { maxHeight: 320 } }}
            onChange={(_, selected) => {
              if (selected && typeof selected !== "string") {
                handleClienteSelect(selected);
                return;
              }
              if (selected === null && hasSelectedCliente) {
                setClienteInput("");
                onClear();
              }
            }}
            options={sugerencias}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.nombre} ${option.apellido}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="Buscar cliente"
                error={!!errors?.nombre || !!errors?.apellido}
                helperText={errors?.nombre || errors?.apellido}
                autoComplete="off"
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Buscar cliente",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 38, sm: 36 },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon
                        sx={{ color: "primary.main", fontSize: "1.1rem" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.25, sm: 2 },
            alignItems: "center",
            flexWrap: "nowrap",
            overflow: "hidden",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={esBooking}
                onChange={(e) => onEsBookingChange(e.target.checked)}
                size="small"
                disabled={titularFieldsLocked}
              />
            }
            label="Booking"
            sx={{
              m: 0,
              fontSize: "0.875rem",
              "& .MuiFormControlLabel-label": {
                fontSize: { xs: "0.76rem", sm: "0.875rem" },
              },
              "& .MuiCheckbox-root": {
                p: { xs: 0.35, sm: 0.5 },
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={esAirbnb}
                onChange={(e) => onEsAirbnbChange(e.target.checked)}
                size="small"
                disabled={titularFieldsLocked}
              />
            }
            label="Airbnb"
            sx={{
              m: 0,
              fontSize: "0.875rem",
              "& .MuiFormControlLabel-label": {
                fontSize: { xs: "0.76rem", sm: "0.875rem" },
              },
              "& .MuiCheckbox-root": {
                p: { xs: 0.35, sm: 0.5 },
              },
            }}
          />
        </Box>
      </Box>
    );
  }
);

ClienteAutocomplete.displayName = "ClienteAutocomplete";

export default ClienteAutocomplete;
